import React from "react";
import { InlineMath } from "react-katex";

const PURPLE = "#8b5cf6";

function pointOnCircle(center, radius, degrees) {
  const radians = (degrees * Math.PI) / 180;
  return {
    x: center.x + radius * Math.cos(radians),
    y: center.y + radius * Math.sin(radians),
  };
}

function angleArc(vertex, first, second, radius) {
  const startAngle = Math.atan2(first.y - vertex.y, first.x - vertex.x);
  let difference = Math.atan2(second.y - vertex.y, second.x - vertex.x) - startAngle;
  while (difference <= -Math.PI) difference += 2 * Math.PI;
  while (difference > Math.PI) difference -= 2 * Math.PI;
  const endAngle = startAngle + difference;

  return {
    path: `M ${vertex.x + radius * Math.cos(startAngle)} ${vertex.y + radius * Math.sin(startAngle)}
      A ${radius} ${radius} 0 0 ${difference >= 0 ? 1 : 0}
      ${vertex.x + radius * Math.cos(endAngle)} ${vertex.y + radius * Math.sin(endAngle)}`,
    middle: startAngle + difference / 2,
  };
}

function MathLabel({ x, y, math, color = "currentColor", width = 44 }) {
  return (
    <foreignObject x={x - width / 2} y={y - 13} width={width} height="26">
      <div style={{ color, fontSize: 14, lineHeight: 1.2, textAlign: "center" }}>
        <InlineMath math={math} />
      </div>
    </foreignObject>
  );
}

export default function SameArcInscribedAnglesDiagram({ angleLabel = "54^{\\circ}", size = 250 }) {
  const O = { x: size / 2, y: size / 2 };
  const R = 92;
  const A = pointOnCircle(O, R, -48);
  const B = pointOnCircle(O, R, 48);
  const P = pointOnCircle(O, R, 180);
  // Drugi wierzchołek leży wyraźnie w dolnej części większego łuku AB.
  // Dzięki temu nie nakłada się ani na punkt A, ani na podpis swojego kąta.
  const Q = pointOnCircle(O, R, 130);
  const arcP = angleArc(P, A, B, 23);
  const arcQ = angleArc(Q, A, B, 23);
  const labelAt = (vertex, arc) => ({
    x: vertex.x + 42 * Math.cos(arc.middle),
    y: vertex.y + 42 * Math.sin(arc.middle),
  });
  const labelP = labelAt(P, arcP);
  const labelQ = labelAt(Q, arcQ);

  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} role="img" aria-label="Dwa kąty wpisane oparte na tym samym łuku AB">
      <circle cx={O.x} cy={O.y} r={R} fill="none" stroke="currentColor" strokeWidth="2" />
      {[P, Q].flatMap((vertex) => [
        <line key={`${vertex.x}-A`} x1={vertex.x} y1={vertex.y} x2={A.x} y2={A.y} stroke="currentColor" strokeWidth="2.2" />,
        <line key={`${vertex.x}-B`} x1={vertex.x} y1={vertex.y} x2={B.x} y2={B.y} stroke="currentColor" strokeWidth="2.2" />,
      ])}
      <path d={arcP.path} fill="none" stroke={PURPLE} strokeWidth="3" strokeLinecap="round" />
      <path d={arcQ.path} fill="none" stroke={PURPLE} strokeWidth="3" strokeLinecap="round" />
      {[A, B, P, Q].map((point, index) => <circle key={index} cx={point.x} cy={point.y} r="3.8" fill="currentColor" />)}
      <MathLabel x={A.x + 15} y={A.y - 10} math="A" />
      <MathLabel x={B.x + 15} y={B.y + 12} math="B" />
      <MathLabel x={P.x - 15} y={P.y} math="P" />
      <MathLabel x={Q.x - 12} y={Q.y + 17} math="Q" />
      <MathLabel x={labelP.x} y={labelP.y} math={angleLabel} color={PURPLE} width={52} />
      <MathLabel x={labelQ.x} y={labelQ.y} math={angleLabel} color={PURPLE} width={52} />
    </svg>
  );
}
